import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Loader, useToast } from '@lms/ui';
import { AttemptConnectionBanner } from '@/components/assessments/AttemptConnectionBanner';
import { QuizPlayer, type QuizQuestion } from '@/components/assessments/QuizPlayer';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useAttemptSync } from '@/hooks/useAttemptSync';
import { api } from '@/lib/api';
import { clockOffsetMs, readClockOffset } from '@/lib/attempt-clock';
import { AttemptStaffFlags } from '@/components/assessments/AttemptStaffFlags';
import { browserAttemptStore } from '@/lib/attempt-offline-store';
import { assessmentAttemptSuccessToast } from '@/lib/user-feedback';
import { useAuthStore } from '@/stores/authStore';

interface AttemptSettings {
  type?: string;
  time_limit_minutes?: number;
}

interface AttemptPayload {
  attempt: {
    id: string;
    number?: number;
    status?: string;
    deadlineAt?: string | null;
    submissionSource?: string | null;
    late?: boolean;
  };
  serverNow?: string;
  graceUntil?: string | null;
  assessment: {
    title: string;
    code?: string;
    currentVersion?: { settings?: AttemptSettings | null };
  };
  questions: {
    id: string;
    stem: string;
    type?: string;
    currentVersion?: { options?: { id: string; label?: string; content: string }[] };
  }[];
  responses?: { questionId: string; response: unknown }[];
}

const TYPE_LABELS: Record<string, string> = {
  exam: 'Examen',
  quiz: 'Quiz',
  survey: 'Encuesta',
};

const TYPE_PREFIX: Record<string, string> = {
  exam: 'Examen: ',
  quiz: 'Quiz: ',
  survey: 'Encuesta: ',
};

export function QuizAttemptPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { toast } = useToast();
  const userId = useAuthStore((state) => state.user?.id);
  const queryClient = useQueryClient();
  const sync = useAttemptSync({
    userId,
    attemptId,
    progressUrl: attemptId ? `/assessments/attempts/${attemptId}/progress` : null,
    submitUrl: attemptId ? `/assessments/attempts/${attemptId}/submit` : null,
    fileUrl: attemptId ? `/assessments/attempts/${attemptId}/questions` : null,
    onSynced: () => {
      void queryClient.invalidateQueries({ queryKey: ['quiz-attempt', attemptId] });
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['quiz-attempt', attemptId],
    queryFn: async () => {
      await sync.flush();
      return api<AttemptPayload>(`/assessments/attempts/${attemptId}`);
    },
    enabled: Boolean(attemptId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    placeholderData: (previous) => previous,
  });

  const initialAnswers = useMemo(() => {
    const map: Record<string, unknown> = {};
    data?.responses?.forEach((row) => {
      map[row.questionId] = row.response;
    });
    if (userId && attemptId) {
      const local = browserAttemptStore().load(userId, attemptId);
      if (local) Object.assign(map, local.answers);
    }
    return map;
  }, [data?.responses, userId, attemptId]);

  useEffect(() => {
    if (!data?.attempt?.deadlineAt && !data?.graceUntil) return;
    sync.rememberTiming(data?.attempt?.deadlineAt ?? null, data?.graceUntil ?? null);
  }, [data?.attempt?.deadlineAt, data?.graceUntil, sync.rememberTiming]);

  useEffect(() => {
    sync.noteServerNow(data?.serverNow);
  }, [data?.serverNow, sync.noteServerNow]);

  const settings = data?.assessment?.currentVersion?.settings ?? {};
  const assessmentType = settings.type ?? 'exam';
  const typeLabel = TYPE_LABELS[assessmentType] ?? 'Examen';
  const titlePrefix = TYPE_PREFIX[assessmentType] ?? '';
  useEffect(() => {
    if (sync.accepted) toast(assessmentAttemptSuccessToast());
  }, [sync.accepted, toast]);

  const closedStatus = data?.attempt?.status;
  const isSubmitted =
    closedStatus === 'submitted' ||
    closedStatus === 'pending_review' ||
    closedStatus === 'approved' ||
    closedStatus === 'rejected' ||
    closedStatus === 'expired_empty' ||
    sync.accepted;

  const questions: QuizQuestion[] =
    data?.questions.map((q, index) => ({
      id: q.id,
      index,
      stem: q.stem,
      type: q.type,
      options: q.currentVersion?.options?.map((opt) => ({ id: opt.id, label: opt.label ?? '', content: opt.content })),
    })) ?? [];

  return (
    <div className="lms-quiz-attempt font-sans">
      <AttemptConnectionBanner offline={!sync.online} />
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <header className="lms-quiz-attempt__header">
          <HubBackLink to="/evaluaciones" />
          <div className="lms-quiz-attempt__header-copy">
            <h1>
              {titlePrefix}
              {data?.assessment?.title ?? 'Intento de evaluación'}
            </h1>
            <p>
              <span className="material-symbols-outlined" aria-hidden="true">
                assignment
              </span>
              Intento {data?.attempt?.number ?? 1} · {typeLabel} · {data?.assessment?.code ?? '—'}
            </p>
            {isSubmitted && (
              <AttemptStaffFlags
                late={data?.attempt?.late}
                submissionSource={data?.attempt?.submissionSource}
              />
            )}
          </div>
        </header>

        {error && !data && !sync.queued && (
          <Alert variant="danger" className="mb-4">
            {(error as Error).message}
          </Alert>
        )}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <QuizPlayer
            questions={questions}
            deadlineAt={data?.attempt?.deadlineAt ?? sync.deadlineAt}
            clockOffsetMs={sync.serverOffsetMs ?? (data?.serverNow ? clockOffsetMs(data.serverNow) : readClockOffset())}
            saveStatus={sync.saveStatus}
            initialAnswers={initialAnswers}
            onLocalChange={(answers) => sync.persistAnswers(answers)}
            onSaveProgress={(answers) => sync.persistAnswers(answers)}
            onUploadFile={(questionId, file) => sync.uploadFile(questionId, file)}
            onSubmit={(answers) => {
              void sync.submit(answers);
            }}
            submitting={sync.sending}
            submitted={isSubmitted}
            queued={sync.queued && !isSubmitted}
            warnBeforeUnload={!isSubmitted && (sync.dirty || sync.queued)}
            submittedLabel="Intento enviado correctamente"
          />
        )}
      </div>
    </div>
  );
}
